import { Input } from '../../../../shared/components/FormControls';
import { useEffect, useState } from 'react';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import { Dice6, Minus, Plus } from 'lucide-react';
import { DualityModeButton, DualityRollButton } from '../DualityRollControl';
import { buildDualityCommand } from '../../utils/dualityModes';

const resolveDieIcon = (text) => {
  const lowered = String(text || '').toLowerCase();
  if (lowered.includes('duality')) return 'duality';
  const match = lowered.match(/d(?:100|20|12|10|8|6|4)/);
  if (!match) return Dice6;
  switch (match[0]) {
    case 'd4':
      return 'd4';
    case 'd6':
      return 'd6';
    case 'd8':
      return 'd8';
    case 'd10':
    case 'd100':
      return 'd10';
    case 'd12':
      return 'd12';
    case 'd20':
      return 'd20';
    default:
      return 'dice';
  }
};

function GmDiceModal({
  visible,
  onClose,
  onRoll,
  mode = 'dark',
  dualityMode: controlledDualityMode,
  onDualityModeChange,
}) {
  const isDark = mode !== 'light';
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 620px)');
    const handleMediaChange = () => {
      setIsPhone(mediaQueryWidth.matches);
    };
    handleMediaChange();
    mediaQueryWidth.addListener(handleMediaChange);
    return () => {
      mediaQueryWidth.removeListener(handleMediaChange);
    };
  }, []);

  const [dualityAdv, setDualityAdv] = useState(false);
  const [dualityDis, setDualityDis] = useState(false);
  const [localDualityMode, setLocalDualityMode] = useState('standard');
  const dualityMode = controlledDualityMode || localDualityMode;
  const [modPlus, setModPlus] = useState('');
  const [modMinus, setModMinus] = useState('');
  const [custom, setCustom] = useState(['', '', '', '', '']);

  const setDualityMode = onDualityModeChange || setLocalDualityMode;

  const toggleDuality = (type) => {
    if (type === 'adv') {
      setDualityAdv((prev) => {
        const next = !prev;
        if (next) setDualityDis(false);
        return next;
      });
    } else if (type === 'dis') {
      setDualityDis((prev) => {
        const next = !prev;
        if (next) setDualityAdv(false);
        return next;
      });
    }
  };

  const handleRoll = (command) => {
    if (typeof onRoll === 'function') onRoll(command);
  };

  const borderColor = isDark ? '2px solid var(--onyx)' : '2px solid #e4E7C5';
  const bgColor = isDark ? 'var(--jet)' : '#f6f7eb';
  const textColor = isDark ? 'var(--ivory)' : 'var(--jet)';

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onClose}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: borderColor,
        backgroundColor: bgColor,
        color: textColor,
        width: isPhone ? 'calc(100% - 34px)' : 470,
        height: isPhone ? 'calc(100% - 114px)' : 610,
        marginTop: isPhone ? '80px' : '',
        //overflowY: 'auto',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <div
          className='trialment_login_form_row'
          style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, textAlign: 'center' }}
        >
          Dice Roller
        </div>

        {/* Duality Row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '8px',
            border: isDark ? '1px solid var(--onyx)' : '1px solid #dcdcdc',
            borderRadius: 6,
          }}
        >
          <DualityModeButton
            mode={dualityMode}
            onModeChange={setDualityMode}
          />
          <Button
            label='Advantage'
            icon={Plus}
            type={dualityAdv ? 'cinnabar' : 'onyx'}
            onPress={() => toggleDuality('adv')}
          />
          <Button
            label='Disadvantage'
            icon={Minus}
            type={dualityDis ? 'cinnabar' : 'onyx'}
            onPress={() => toggleDuality('dis')}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>+</span>
            <Input
              type='text'
              value={modPlus}
              onChange={(e) => setModPlus(e.target.value)}
              placeholder='0'
              style={{ width: 130 }}
            />
            <span style={{ marginLeft: 4 }}>-</span>
            <Input
              type='text'
              value={modMinus}
              onChange={(e) => setModMinus(e.target.value)}
              placeholder='0'
              style={{ width: 130 }}
            />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <DualityRollButton
              mode={dualityMode}
              onRoll={() => handleRoll(buildDualityCommand({
                dualityMode,
                dualityAdv,
                dualityDis,
                modPlus,
                modMinus,
              }))}
            />
          </div>
        </div>

        {/* Custom dice rows */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            overflowY: 'auto',
            paddingTop: '10px',
          }}
        >
          {custom.map((value, idx) => (
            <div
              key={idx}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Input
                type='text'
                placeholder={idx === 0 ? 'e.g. 1d20, 2d6 + 1d10, 2d10 + 4' : ''}
                value={value}
                onChange={(e) => {
                  const next = [...custom];
                  next[idx] = e.target.value;
                  setCustom(next);
                }}
                style={{ flex: 1 }}
              />
              <Button
                icon={resolveDieIcon(value)}
                type='black-white'
                description={value.trim() ? `Roll ${value}` : ''}
                descriptionUp={isPhone ? true : false}
                disabled={!value.trim()}
                onPress={() => handleRoll(value)}
              />
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: '8px',
            marginBottom: '8px',
          }}
        >
          <Button
            label='Clear Dice'
            type='onyx'
            onPress={() => handleRoll('Clear')}
          />
          <Button
            label='Close'
            type={isDark ? 'onyx' : 'black-white'}
            onPress={onClose}
          />
        </div>
      </div>
    </Rodal>
  );
}

export default GmDiceModal;
